import React, { useState, useEffect } from "react";
import { MbmProvider } from "@amzn/react-arb-tools";
import { AppContext, ELIGIBILITY_STATUS, extractFromUserInfo } from "./shared";
import AppContent from "./appContent";
import { initAwsRum, sendCustomRUMEvent } from "./shared/utils/awsRUM";
import Loader from "@amzn/meridian/loader";
import ParseService from "./shared/utils/parseService";
import { getUser } from "../service/auths";
import { isLocal } from "./shared/utils/utils";
import { LocalizationContextBuilder } from "@amzn/arb-tools";
import arbManifest from "../i18n/translations/arbManifest";
import { resolveBundleUrlWithFallbacks } from "../helpers";
import { DEFAULT_LOCALE } from "../constants";
import { ScrollToTop } from "src/components/shared/components/scrollToTop";
import { ErrorBoundary } from "src/components/shared/components/errorboundary";
import { json } from "react-router-dom";

const App = () => {
  const [awsRum, setAwsRum] = useState(null);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [processPathPairs, setProcessPathPairs] = useState([]);
  const [hasConfirmedEligibility, setHasConfirmedEligibility] = useState(
    ELIGIBILITY_STATUS.NO
  );
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [language, setLanguage] = useState(DEFAULT_LOCALE);

  // countryCodeValue and businessUnitValue should be ready-only once read from Cognito user
  // these setCountryCodeValue and setBusinessUnitValue should only used for testing in dev envs
  const [countryCodeValue, setCountryCodeValue] = useState("");
  const [businessUnitValue, setBusinessUnitValue] = useState("");

  useEffect(() => {
    initAwsRum()
      .then((awsRum) => setAwsRum(awsRum))
      .catch((e) => {
        console.error(e.message);
      });
  }, []);

  useEffect(() => {
    getUser()
      .then((userInfo) => {
        setUserInfo(userInfo);
      })
      .catch((e) => {
        // TO-DO: explore rum send error event
        console.error(e.message);
      });
  }, []);

  useEffect(() => {
    if (userInfo) {
      const { siteCode } = extractFromUserInfo(userInfo);
      ParseService.parseCSVFiles(siteCode)
        .then((res) => {
          setProcessPathPairs(res);
        })
        .catch((e) => {
          console.error(e.message);
        });
      ParseService.parseLocaleCSVFile(siteCode)
        .then((res) => {
          setLanguage(res);
          setIsLoading(false);
        })
        .catch((e) => {
          console.error(e.message);
        });
      const { countryCode, businessUnit } = extractFromUserInfo(userInfo);
      setCountryCodeValue(countryCode);
      setBusinessUnitValue(businessUnit);
      if (!isLocal()) {
        sendCustomRUMEvent("CognitoUser", {
          ["User"]: JSON.stringify(userInfo),
        });
      }
    }
  }, [userInfo]);

  if (isLoading) {
    return <Loader></Loader>;
  }

  const { alias, userName, siteCode } = extractFromUserInfo(userInfo);

  if (!isLocal()) {
    awsRum.addSessionAttributes({
      alias: alias,
      site: siteCode,
      businessUnit: businessUnitValue,
    });
  }

  const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
      .withLocale(DEFAULT_LOCALE)
      .withDefaultLocale(DEFAULT_LOCALE)
      .build(),
    resolveBundleUrl: (resourcePackPath, locale, bundle) =>
      resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
  };

  // ini value for AppContext, usage: { siteCode, sideMenuOpen, etc.. } = useAppContext();
  const value = {
    alias, // string, e.g., "wwangam"
    userName, // string, e.g., "Wei Wang"
    siteCode, // string, e.g., "SEA94"
    countryCode: { countryCodeValue, setCountryCodeValue }, // string, e.g., "USA"
    businessUnit: { businessUnitValue, setBusinessUnitValue }, // string, e.g., "AMZID"
    processPathPairs: { processPathPairs, setProcessPathPairs }, // [[processPath, processPathEligiblity],..], e.g., [["PICK", "Y"], ..]
    locale: { language, setLanguage }, // string, e.g., "en-US"
    sideMenu: { sideMenuOpen, setSideMenuOpen }, // boolean
    eligibility: { hasConfirmedEligibility, setHasConfirmedEligibility }, // ELIGIBILITY_STATUS
  };

  return (
    <MbmProvider {...mbmOptions}>
      <AppContext.Provider value={value}>
        <ErrorBoundary>
          <ScrollToTop>
            <AppContent></AppContent>
          </ScrollToTop>
        </ErrorBoundary>
      </AppContext.Provider>
    </MbmProvider>
  );
};

export default App;
